import { useEffect, useState } from "react";
import { useIncompleteActionsModal } from "@circle-workflows/components/ViewWorkflowModal/Header/HeaderActions/IncompleteActionsModal";
import { useWorkflow } from "@circle-workflows/components/WorkflowForm/useWorkflow";
import type { Action } from "@circle-workflows/generated/WorkflowsApi.types";
import { useDeleteAction } from "@circle-workflows/hooks/useDeleteAction";
import { useFetchWorkflow } from "@circle-workflows/hooks/useFetchWorkflow";
import { useUpsertAction } from "@circle-workflows/hooks/useUpsertAction";
import { useUpsertActionsArray } from "@circle-workflows/hooks/useUpsertActionsArray";
import { noop, omit } from "lodash";
import isFunction from "lodash/isFunction";
import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import {
  isMarketingHubRequired,
  isPlanCheckFailed,
} from "@/react/components/SettingsApp/Workflows/workflowApiErrorHelper";
import { nextPlanTierForWorkflowUpgrade } from "@/react/helpers/planHelpers";
import { getWorkflowSource } from "@circle-react/components/SettingsApp/Workflows/hooks/useOpenedFromPage";
import { usePunditUserContext } from "@circle-react/contexts";
import { useBlockWithConnectorData } from "@circle-react-shared/BlockWithConnector/BlockWithConnectorDataProvider";
import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";
import { useToast } from "@circle-react-uikit/ToastV2";
import { WORKFLOW_STATUS, getDefaultAction } from "../../constants";
import type { ActionWithRules } from "../../types";
import { useIsAiAutomationsV2Enabled } from "../Actions/AiAgents/renamePostTitle/useIsAiAutomationsV2Enabled";
import { getActionHasValue } from "../WorkflowForm/getActionHasValue";
import { ACTIONS, useActionValidation } from "./useActionValidation";
import { useActionsIds } from "./useActionsIds";

export const useActions = () => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const { onCloseItemAndOpenNext, setActiveItemId } =
    useBlockWithConnectorData();
  const toast = useToast();
  const incompleteActionsModal = useIncompleteActionsModal();
  const isAiAutomationsV2Enabled = useIsAiAutomationsV2Enabled();

  const {
    watch,
    setError,
    trigger: revalidate,
    setValue,
    resetField,
    formState,
  } = useFormContext();

  const { actionsIds, itemsIds } = useActionsIds();
  const {
    doesActionhasServerId,
    areSomeActionsDirty,
    areSomeActionsTouched,
    areAllActionsFilled,
    areAllActionsWithServerIds,
    areAllActionsSaved,
  } = useActionValidation();

  const { errors, defaultValues } = formState;
  const formActions = watch(ACTIONS, []);
  const hasActions = formActions.length > 0;
  const hasErrors = Object.keys(errors).length > 0;

  const { remove, replace } = useFieldArray({
    name: ACTIONS,
  });

  const { status, id } = useWorkflow();
  const { refetch: refetchWorkflow } = useFetchWorkflow({ workflowId: id });

  const { currentCommunity } = usePunditUserContext();
  const currentPlanTier = currentCommunity?.current_plan_tier;
  const upgradePlanModal = useUpgradePlanModal();
  const { refetch: refetchPundit } = usePunditUserContext();
  const showUpgradePlanModal = (error: any) => {
    void upgradePlanModal.show({
      ...(isPlanCheckFailed(error) && {
        planTier: nextPlanTierForWorkflowUpgrade(currentPlanTier),
      }),
      subheading: error.body.message,
      isEmailHubRequired: isMarketingHubRequired(error),
      source: getWorkflowSource(),
      onSuccess: async () => {
        await refetchPundit();
      },
      successButtonLabel: t(
        "settings.workflows.upgrade_modal.activate_this_workflow",
      ),
      usedForWorkflows: true,
      showPlanDetails: true,
    });
  };

  useEffect(() => {
    if (isFirstRender && !hasActions) {
      setIsFirstRender(false);
      replace(defaultValues?.actions);
    }
  }, [defaultValues?.actions, hasActions, isFirstRender, replace]);

  const { mutate: deleteAction, isLoading: isActionDeleting } =
    useDeleteAction();

  const {
    mutateAsync: upsertActionAsync,
    isLoading: isUpdatingAction,
    isError: isActionError,
  } = useUpsertAction();

  const onUpsertActionsSuccess = async (
    response: ActionWithRules[] = [],
    callback: (() => void) | undefined,
  ) => {
    const actions = response.map(action => omit(action, "errors"));
    replace(actions);
    resetField(ACTIONS, { defaultValue: actions });

    isFunction(callback) ? callback() : onCloseItemAndOpenNext();

    try {
      await refetchWorkflow();
    } catch (error) {
      console.error(error);
    }
  };

  const onUpsertActionsError = (error: any) => {
    const actionsError = error?.body?.data?.actions || [];

    actionsError.forEach((error: any, index: number) => {
      for (const field in error) {
        setError(`${ACTIONS}[${index}].${field}`, {
          type: "server",
          message: error[field],
        });
      }
    });

    if (isPlanCheckFailed(error) || isMarketingHubRequired(error)) {
      return showUpgradePlanModal(error);
    }
    return toast.error(error.message);
  };

  const {
    mutateAsync: upsertActionsAsync,
    isLoading: isSavingActions,
    isError: isActionsError,
  } = useUpsertActionsArray({
    onSuccess: onUpsertActionsSuccess,
    onError: onUpsertActionsError,
  });

  const replaceActionsAndSetActiveId = (actions: Action[], idIndex: number) => {
    replace(actions);
    resetField(ACTIONS, { defaultValue: actions });

    const id = actions[idIndex]?.id;
    setActiveItemId(id);
  };

  const addActionAtStart = () => {
    const newAction = getDefaultAction();
    const currentActions = watch(ACTIONS, []);
    const actions = [newAction, ...currentActions];

    replaceActionsAndSetActiveId(actions, 0);
  };

  const addActionAfterPosition = ({ index }: { index: number }) => {
    const newAction = getDefaultAction();
    const currentActions = watch(ACTIONS, []);
    const actions = [
      ...currentActions.slice(0, index + 1),
      newAction,
      ...currentActions.slice(index + 1),
    ];

    replaceActionsAndSetActiveId(actions, index + 1);
  };

  const removeActionAtPosition = async (index: number) => {
    try {
      const actions = watch(ACTIONS, []);
      const action = actions[index];
      const hasServerId = doesActionhasServerId(action);

      remove(index);
      const updatedActions = watch(ACTIONS, []);
      resetField(ACTIONS, { defaultValue: updatedActions });

      if (!hasServerId) {
        return;
      }

      deleteAction(action.id);

      if (updatedActions.length === 0 && status === WORKFLOW_STATUS.ACTIVE) {
        setValue("status", WORKFLOW_STATUS.INACTIVE);
        await refetchWorkflow();

        await incompleteActionsModal.show({
          i18nBase:
            "settings.workflows.edit.incomplete_workflow_no_actions_modal",
          onAction: () => {
            void incompleteActionsModal.hide();
          },
        });
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : String(error));
    }
  };

  const saveActions = async (onSuccess = noop) => {
    const areActionsValid = await revalidate(ACTIONS);

    if (!areAllActionsFilled) {
      formActions.forEach((action: Action, index: number) => {
        if (!getActionHasValue(action, isAiAutomationsV2Enabled)) {
          setError(`actions[${index}]`, {
            type: "manual",
            message: t("settings.workflows.form.step_incomplete"),
          });
        }
      });
      return;
    }

    if (!areActionsValid || !areAllActionsFilled) return;

    try {
      const actions = watch(ACTIONS) || [];
      const response = await upsertActionsAsync(actions);
      void onUpsertActionsSuccess(response, onSuccess);
    } catch (error) {
      onUpsertActionsError(error);
    }
  };

  const saveAction = async ({
    path,
    action,
    skipOpenNext = false,
  }: {
    path: string;
    action: any;
    skipOpenNext: boolean;
  }) => {
    try {
      const updatedAction: Action = await upsertActionAsync(action);
      setValue(path, updatedAction);
      resetField(path, { defaultValue: updatedAction });

      if (
        skipOpenNext &&
        updatedAction &&
        typeof updatedAction === "object" &&
        "id" in updatedAction
      ) {
        setActiveItemId(updatedAction.id);
      } else {
        onCloseItemAndOpenNext();
      }

      void refetchWorkflow();
    } catch (error: unknown) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : String(error));
    }
  };

  return {
    actionsIds,
    isFirstRender,
    addActionAfterPosition,
    addActionAtStart,
    formActions,
    hasActions,
    hasErrors,
    isActionError,
    doesActionhasServerId,
    isActionsError,
    isSavingActions,
    isUpdatingAction,
    itemsIds,
    isActionDeleting,
    removeActionAtPosition,
    saveAction,
    saveActions,
    areAllActionsFilled,
    areSomeActionsDirty,
    areSomeActionsTouched,
    areAllActionsWithServerIds,
    areAllActionsSaved,
  };
};
